import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, CalendarCheck, Check, Phone, X } from "lucide-react";
import {
  BOOKING_CHOICE,
  BOOKING_LINKS,
  CONCERN_OPTIONS,
  PHONE_DISPLAY,
  PHONE_TEL,
  VISITS,
  VISIT_INCLUDES,
  visitForConcern,
} from "@/lib/campaign";
import { track } from "@/lib/track";
import { BookingDialog } from "./BookingDialog";

const TOTAL_STEPS = 5;

const concernSentence = {
  hormone: "I'm interested in help with menopause, perimenopause, or hormone health.",
  functional: "I'm interested in functional medicine or broader wellness support.",
  joint: "I'm looking for help with joint or muscle pain.",
  unsure: "I'm not sure yet which type of visit fits me best.",
};

export const buildPrefillMessage = (answers) => {
  const visit = answers.officeHelp ? null : visitForConcern(answers.concern);
  const lines = [concernSentence[answers.concern]];
  if (answers.newPatient === "yes") lines.push("I'm new to All-In Precision Wellness.");
  if (answers.approach === "yes") lines.push("The private-pay approach works for me.");
  let msg = `Hi Dr. Crosson's team,\n\nI completed the appointment guide and would like to talk before booking.\n\n${lines.join("\n")}\n\n`;
  if (visit) {
    msg += `Suggested appointment: ${visit.name}, ${visit.duration}.\nExpected visit cost: ${visit.price}.\n\n`;
  } else {
    msg += "I'd like help choosing the right visit type.\n\n";
  }
  msg += "My questions: ";
  return msg;
};

const Option = ({ selected, onClick, children, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    aria-pressed={selected}
    className={`w-full text-left px-5 py-4 border-2 text-base sm:text-lg transition-colors ${
      selected
        ? "border-[var(--brand-navy)] bg-[var(--brand-cream)] font-semibold text-[var(--brand-navy)]"
        : "border-[var(--brand-gold-soft)] bg-white hover:border-[var(--brand-gold)]"
    }`}
  >
    <span className="flex items-center justify-between gap-3">
      {children}
      {selected && <Check className="h-5 w-5 shrink-0 text-[var(--brand-gold)]" aria-hidden="true" />}
    </span>
  </button>
);

const PrimaryBtn = ({ onClick, children, testId, disabled }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    disabled={disabled}
    className="w-full sm:w-auto inline-flex items-center justify-center bg-[var(--brand-navy)] text-white text-lg font-semibold px-10 py-3.5 hover:bg-[var(--brand-navy-deep)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
  >
    {children}
  </button>
);

const SecondaryBtn = ({ onClick, children, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className="w-full sm:w-auto inline-flex items-center justify-center border-2 border-[var(--brand-navy)] text-[var(--brand-navy)] text-lg font-semibold px-8 py-3 bg-white hover:bg-[var(--brand-cream)] transition-colors"
  >
    {children}
  </button>
);

export const Quiz = ({ variant, onClose, onAskQuestion }) => {
  const [history, setHistory] = useState([1]);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [answers, setAnswers] = useState({
    concern: "hormone",
    newPatient: null,
    approach: null,
    officeHelp: false,
  });
  const step = history[history.length - 1];
  const visit = answers.officeHelp ? null : visitForConcern(answers.concern);

  const go = (next) => {
    setHistory((h) => [...h, next]);
    track(variant, "quiz_step", next);
    if (next === 5) track(variant, "quiz_complete");
  };
  const back = () => setHistory((h) => (h.length > 1 ? h.slice(0, -1) : h));
  const set = (patch) => setAnswers((a) => ({ ...a, ...patch }));

  const askQuestion = () => onAskQuestion(buildPrefillMessage(answers));

  return (
    <div
      data-testid="quiz-overlay"
      className="fixed inset-0 z-50 bg-[var(--brand-bg)] overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-label="Appointment guide"
    >
      <div className="sticky top-0 bg-white/95 backdrop-blur-md border-b border-[var(--brand-gold-soft)] z-10">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/images/logo.png" alt="All-In Precision Wellness" className="h-10 w-10 object-contain" />
            <span className="text-sm font-semibold text-[var(--brand-navy)]">Appointment guide</span>
          </div>
          <button
            data-testid="quiz-close-button"
            onClick={onClose}
            aria-label="Close guide"
            className="p-2 text-[var(--brand-muted)] hover:text-[var(--brand-navy)]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="h-1 bg-[var(--brand-gold-soft)]" aria-hidden="true">
          <div
            data-testid="quiz-progress-bar"
            className="h-1 bg-[var(--brand-gold)] transition-all duration-500"
            style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
          />
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8 sm:py-12 pb-24">
        <p className="text-sm text-[var(--brand-muted)] mb-2">
          Step {step} of {TOTAL_STEPS}
        </p>
        <AnimatePresence mode="wait">
          <motion.div
            key={`${step}-${answers.newPatient === "no" ? "existing" : "new"}`}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.3 }}
          >
            {step === 1 && (
              <div data-testid="quiz-step-1">
                <h2 className="font-serif-brand text-2xl sm:text-3xl font-medium text-[var(--brand-navy)]">
                  What would you like help with?
                </h2>
                <div className="mt-6 space-y-3">
                  {CONCERN_OPTIONS.map((o, i) => (
                    <Option
                      key={o.value}
                      testId={`quiz-concern-option-${o.value}`}
                      selected={answers.concern === o.value}
                      onClick={() => set({ concern: o.value, officeHelp: false })}
                    >
                      {o.label}
                    </Option>
                  ))}
                </div>
                <div className="mt-8">
                  <PrimaryBtn testId="quiz-step-1-continue" onClick={() => go(2)}>
                    Continue
                  </PrimaryBtn>
                </div>
              </div>
            )}

            {step === 2 && answers.newPatient !== "no" && (
              <div data-testid="quiz-step-2">
                <h2 className="font-serif-brand text-2xl sm:text-3xl font-medium text-[var(--brand-navy)]">
                  Are you new to All-In Precision Wellness?
                </h2>
                <div className="mt-6 space-y-3">
                  <Option
                    testId="quiz-new-patient-yes"
                    selected={answers.newPatient === "yes"}
                    onClick={() => {
                      set({ newPatient: "yes" });
                      go(3);
                    }}
                  >
                    Yes, I&rsquo;m a new patient
                  </Option>
                  <Option
                    testId="quiz-new-patient-no"
                    selected={false}
                    onClick={() => set({ newPatient: "no" })}
                  >
                    No, I&rsquo;ve been seen here before
                  </Option>
                </div>
              </div>
            )}

            {step === 2 && answers.newPatient === "no" && (
              <div data-testid="quiz-existing-patient-screen">
                <h2 className="font-serif-brand text-2xl sm:text-3xl font-medium text-[var(--brand-navy)]">
                  Welcome back.
                </h2>
                <p className="mt-4 text-lg leading-relaxed">
                  This guide is for new-patient visits. To schedule your next appointment as an
                  existing patient, please call the office so the team can find the right visit type
                  for you.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <a
                    data-testid="quiz-existing-call-button"
                    href={PHONE_TEL} data-call-location="quiz"
                    onClick={() => track(variant, "call_click")}
                    className="inline-flex items-center justify-center gap-2 bg-[var(--brand-navy)] text-white text-lg font-semibold px-8 py-3.5"
                  >
                    <Phone className="h-5 w-5" aria-hidden="true" /> Call {PHONE_DISPLAY}
                  </a>
                  <SecondaryBtn
                    testId="quiz-existing-back-button"
                    onClick={() => set({ newPatient: null })}
                  >
                    I&rsquo;m actually a new patient
                  </SecondaryBtn>
                </div>
              </div>
            )}

            {step === 3 && answers.concern !== "unsure" && (
              <div data-testid="quiz-step-3">
                <h2 className="font-serif-brand text-2xl sm:text-3xl font-medium text-[var(--brand-navy)]">
                  Here&rsquo;s what your visit includes.
                </h2>
                <div className="mt-6 bg-white border border-[var(--brand-gold-soft)] p-6">
                  <p className="text-lg font-semibold text-[var(--brand-navy)]">{visit.name}</p>
                  <p className="mt-1 text-base text-[var(--brand-muted)]">
                    {visit.duration} with Dr. Jill Crosson, DO, MBA &bull; {visit.price}
                  </p>
                  <ul className="mt-5 space-y-2.5">
                    {(answers.concern === "joint"
                      ? [
                          "Time directly with Dr. Crosson",
                          "A detailed health history focused on your pain and activity",
                          "Review of available previous labs and imaging when relevant",
                          "A physical examination",
                          "Discussion of next steps and any testing she recommends",
                        ]
                      : VISIT_INCLUDES
                    ).map((x) => (
                      <li key={x} className="flex items-start gap-2.5 text-base">
                        <Check className="h-4 w-4 mt-1 shrink-0 text-[var(--brand-gold)]" aria-hidden="true" />
                        {x}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-8">
                  <PrimaryBtn testId="quiz-step-3-continue" onClick={() => go(4)}>
                    Continue
                  </PrimaryBtn>
                </div>
              </div>
            )}

            {step === 3 && answers.concern === "unsure" && (
              <div data-testid="quiz-step-3-unsure">
                <h2 className="font-serif-brand text-2xl sm:text-3xl font-medium text-[var(--brand-navy)]">
                  Here are the two first-visit options.
                </h2>
                <p className="mt-3 text-base leading-relaxed text-[var(--brand-muted)]">
                  You don&rsquo;t need to decide alone. Pick the one that sounds closest, or let the
                  office help you choose.
                </p>
                <div className="mt-6 space-y-3">
                  <Option
                    testId="quiz-unsure-pick-hormone"
                    selected={false}
                    onClick={() => {
                      set({ concern: "hormone", officeHelp: false });
                      go(4);
                    }}
                  >
                    <span>
                      <span className="block font-semibold">Hormone health or functional medicine</span>
                      <span className="block text-sm text-[var(--brand-muted)]">
                        {VISITS.hormone.duration} with Dr. Crosson &bull; {VISITS.hormone.price}
                      </span>
                    </span>
                  </Option>
                  <Option
                    testId="quiz-unsure-pick-joint"
                    selected={false}
                    onClick={() => {
                      set({ concern: "joint", officeHelp: false });
                      go(4);
                    }}
                  >
                    <span>
                      <span className="block font-semibold">Joint or muscle pain</span>
                      <span className="block text-sm text-[var(--brand-muted)]">
                        {VISITS.joint.duration} with Dr. Crosson &bull; {VISITS.joint.price}
                      </span>
                    </span>
                  </Option>
                  <Option
                    testId="quiz-unsure-office-help"
                    selected={false}
                    onClick={() => {
                      set({ concern: "unsure", officeHelp: true });
                      go(5);
                    }}
                  >
                    I&rsquo;d like the office to help me choose
                  </Option>
                </div>
              </div>
            )}

            {step === 4 && (
              <div data-testid="quiz-step-4">
                <h2 className="font-serif-brand text-2xl sm:text-3xl font-medium text-[var(--brand-navy)]">
                  Does this approach work for you?
                </h2>
                <div className="mt-6 bg-white border border-[var(--brand-gold-soft)] p-6 space-y-4 text-base leading-relaxed">
                  <p>
                    All-In Precision Wellness is a private-pay practice. We don&rsquo;t accept
                    insurance, which lets Dr. Crosson set aside dedicated, unhurried time for your
                    visit.
                  </p>
                  <p>
                    Visits are billed at $100 per 15 minutes, based on time spent. Your planned{" "}
                    {visit?.duration} visit is expected to be {visit?.price}. You will never be
                    billed for time not spent.
                  </p>
                  <p>
                    We also provide a superbill and receipt for your records, which you may submit
                    to your insurance if you choose.
                  </p>
                </div>
                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <PrimaryBtn
                    testId="quiz-approach-yes"
                    onClick={() => {
                      set({ approach: "yes" });
                      go(5);
                    }}
                  >
                    Yes, continue
                  </PrimaryBtn>
                  <SecondaryBtn
                    testId="quiz-approach-talk"
                    onClick={() => {
                      set({ approach: "talk" });
                      onAskQuestion(buildPrefillMessage({ ...answers, approach: null }));
                    }}
                  >
                    I&rsquo;d like to talk with the office first
                  </SecondaryBtn>
                </div>
              </div>
            )}

            {step === 5 && (
              <div data-testid="quiz-step-5">
                <h2 className="font-serif-brand text-2xl sm:text-3xl font-medium text-[var(--brand-navy)]">
                  Your next step
                </h2>
                <div className="mt-6 bg-white border border-[var(--brand-gold-soft)] p-6">
                  <p className="text-sm font-semibold uppercase tracking-[0.15em] text-[var(--brand-gold)]">
                    Your visit summary
                  </p>
                  <p className="mt-3 text-base">{concernSentence[answers.concern]}</p>
                  {visit ? (
                    <p data-testid="quiz-summary-visit" className="mt-2 text-lg font-semibold text-[var(--brand-navy)]">
                      {visit.name}: {visit.duration} with Dr. Crosson, {visit.price}
                    </p>
                  ) : (
                    <p data-testid="quiz-summary-visit" className="mt-2 text-base">
                      The office will help you choose between the 60-minute ($400) and 45-minute
                      ($300) first visits.
                    </p>
                  )}
                </div>
                {(() => {
                  const bookingLink = visit ? BOOKING_LINKS[visit.key] : null;
                  return bookingLink ? (
                    <>
                      <p className="mt-6 text-lg leading-relaxed">
                        Would you like to request your visit times or talk with our team first?
                      </p>
                      <div className="mt-6 flex flex-col sm:flex-row gap-4">
                        <button
                          data-testid="quiz-book-appointment-button"
                          onClick={() => {
                            track(variant, "booking_click");
                            setBookingOpen(true);
                          }}
                          className="inline-flex items-center justify-center gap-2 bg-[var(--brand-navy)] text-white text-lg font-semibold px-8 py-3.5 hover:bg-[var(--brand-navy-deep)] transition-colors"
                        >
                          <CalendarCheck className="h-5 w-5" aria-hidden="true" /> Request my times
                        </button>
                        <SecondaryBtn testId="quiz-send-question-button" onClick={askQuestion}>
                          I have questions first
                        </SecondaryBtn>
                      </div>
                      <p className="mt-4 bg-[var(--brand-cream)] border border-[var(--brand-gold-soft)] px-4 py-3 text-base">
                        You&rsquo;ll pick your top two times for a{" "}
                        <span className="font-semibold text-[var(--brand-navy)]">
                          &ldquo;{BOOKING_CHOICE[visit.key]}&rdquo;
                        </span>{" "}
                        visit, and the office will reach out to confirm one of them.
                      </p>
                      <p className="mt-4 text-sm text-[var(--brand-muted)]">
                        Requesting times takes under a minute, right here on the
                        page. Prefer to talk? Call{" "}
                        <a href={PHONE_TEL} data-call-location="quiz" onClick={() => track(variant, "call_click")} className="font-semibold underline">
                          {PHONE_DISPLAY}
                        </a>
                        .
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="mt-6 text-lg leading-relaxed">
                        Online booking isn&rsquo;t available yet. To arrange your visit, please call{" "}
                        <span className="font-semibold">{PHONE_DISPLAY}</span>.
                      </p>
                      <div className="mt-6 flex flex-col sm:flex-row gap-4">
                        <a
                          data-testid="quiz-call-to-book-button"
                          href={PHONE_TEL} data-call-location="quiz"
                          onClick={() => track(variant, "call_click")}
                          className="inline-flex items-center justify-center gap-2 bg-[var(--brand-navy)] text-white text-lg font-semibold px-8 py-3.5 hover:bg-[var(--brand-navy-deep)] transition-colors"
                        >
                          <Phone className="h-5 w-5" aria-hidden="true" /> Call to Book
                        </a>
                        <SecondaryBtn testId="quiz-send-question-button" onClick={askQuestion}>
                          Send a Question
                        </SecondaryBtn>
                      </div>
                    </>
                  );
                })()}
                <p className="mt-4 text-sm text-[var(--brand-muted)]">
                  Sending a question shares an editable summary of your selections with the office.
                  Nothing is sent until you review and submit it.
                </p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {step > 1 && (
          <button
            data-testid="quiz-back-button"
            onClick={back}
            className="mt-10 inline-flex items-center gap-2 text-base font-medium text-[var(--brand-muted)] hover:text-[var(--brand-navy)] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back
          </button>
        )}
      </div>
      {visit && (
        <BookingDialog
          open={bookingOpen}
          onClose={() => setBookingOpen(false)}
          choiceLabel={BOOKING_CHOICE[visit.key]}
          variant={variant}
        />
      )}
    </div>
  );
};
